import { VERIFY_CASE } from '@/mocks/verifyDesk';
import SectionHeading from './SectionHeading';

function statusStyle(status: string) {
  switch (status) {
    case '已交叉印证':
      return 'bg-primary-500 text-background-50';
    case '待补充':
      return 'bg-accent-500 text-background-50';
    case '需二次核对':
      return 'bg-secondary-500 text-background-50';
    default:
      return 'bg-background-200 text-foreground-500 line-through';
  }
}

export default function VerifyDesk() {
  return (
    <section id="verify" className="border-b border-background-200 bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <SectionHeading
            index="03"
            kicker="多线索扫核工作台"
            title="围观求证，看瓜如何一步一步被坐实"
            sub="同一指向的多条独立线索相互印证，才值得被采信。下面是《XL-26-0712》征集令第一轮扫核的公开过程。"
          />
          <div className="shrink-0 space-y-2 md:pb-1">
            <p className="flex items-center gap-2 text-[12px] font-medium text-foreground-600">
              <i className="ri-time-line" aria-hidden="true"></i>
              {VERIFY_CASE.updatedAt}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              {['已交叉印证', '待补充', '需二次核对', '已排除'].map((s) => (
                <span key={s} className={'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ' + statusStyle(s)}>
                  <i className="ri-circle-fill text-[6px]" aria-hidden="true"></i>
                  {s}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-12">
          {/* rows */}
          <div className="lg:col-span-7">
            <div className="flex flex-wrap items-center gap-3 rounded-t-lg border border-b-0 border-background-200 bg-foreground-950 px-5 py-4 text-foreground-50 md:px-7">
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
                <i className="ri-focus-2-line text-lg" aria-hidden="true"></i>
              </span>
              <div>
                <p className="text-[12px] text-foreground-400">{VERIFY_CASE.tag} · {VERIFY_CASE.caseNo}</p>
                <h3 className="font-heading text-base font-bold md:text-lg">{VERIFY_CASE.caseTitle}</h3>
              </div>
            </div>
            <div className="rounded-b-lg border border-background-200 bg-background-100 p-5 md:p-7">
              <ul className="space-y-3">
                {VERIFY_CASE.rows.map((row) => (
                  <li key={row.id} className="flex flex-col gap-3 rounded-md border border-background-200 bg-background-50 p-4 md:flex-row md:items-start md:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-heading text-sm font-bold text-foreground-900">{row.origin}</span>
                        <span className="text-[11px] text-foreground-400">#{row.id.toUpperCase()}</span>
                      </div>
                      <p className="mt-1.5 text-[14px] leading-relaxed text-foreground-700">{row.summary}</p>
                      <p className="mt-2 inline-flex flex-wrap items-center gap-1.5 text-[12px] text-foreground-500">
                        <i className="ri-focus-3-line text-primary-600" aria-hidden="true"></i>
                        指向：{row.keyPoint}
                      </p>
                    </div>
                    <span className={'inline-flex shrink-0 items-center justify-center whitespace-nowrap self-start rounded-md px-2.5 py-1 text-[12px] font-bold ' + statusStyle(row.status)}>
                      {row.status}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* method + conclusion */}
          <aside className="lg:col-span-5">
            <div className="flex h-full flex-col gap-5">
              <div className="rounded-lg border border-accent-200 bg-accent-100/70 p-6 md:p-7">
                <div className="flex items-center gap-2 text-accent-900">
                  <i className="ri-quill-pen-line text-xl" aria-hidden="true"></i>
                  <h4 className="font-heading text-lg font-bold">第一轮结论</h4>
                </div>
                <p className="mt-3 text-[14px] leading-relaxed text-accent-950/90">{VERIFY_CASE.conclusion}</p>
                <div className="mt-5 flex items-center gap-2 rounded-md bg-background-50 px-4 py-3 text-[13px] text-foreground-700">
                  <i className="ri-eye-line text-accent-700" aria-hidden="true"></i>
                  本站仅做线索中转与求证展示，不替代权威裁定。
                </div>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-7">
                <h4 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <i className="ri-shield-check-line text-primary-600" aria-hidden="true"></i>
                  扫核三问
                </h4>
                <ul className="mt-4 space-y-3">
                  {[
                    ['来源可不可考？', '能否找到原始载体与可信的时间戳。'],
                    ['是不是独立信源？', '多路互不知情的线索比单一转发更可信。'],
                    ['指向一不一致？', '关键事实能被不同线索反复印证到同一结论。'],
                  ].map(([t, d], i) => (
                    <li key={t} className="flex gap-3">
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-500 font-heading text-[13px] font-bold text-background-50">
                        {i + 1}
                      </span>
                      <div>
                        <p className="text-[14px] font-semibold text-foreground-900">{t}</p>
                        <p className="mt-0.5 text-[13px] text-foreground-600">{d}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}